import { lazy, useEffect } from 'react'
import { AgentErrorFallback } from '../components/common/AgentErrorFallback'
import { AgentLoadingFallback } from '../components/common/AgentLoadingFallback'

type ImportFn = () => Promise<{ default: React.ComponentType }>

function enableAgentStyles(styles: HTMLStyleElement[]) {
  styles.forEach((el) => {
    if (el.sheet) el.sheet.disabled = false
  })
}

function disableAgentStyles(styles: HTMLStyleElement[]) {
  styles.forEach((el) => {
    if (el.sheet) el.sheet.disabled = true
  })
}

function observeNewStyles(agentStyles: HTMLStyleElement[]): MutationObserver {
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof HTMLStyleElement && !agentStyles.includes(node)) {
          agentStyles.push(node)
        }
      }
    }
  })
  observer.observe(document.head, { childList: true })
  return observer
}

function makeAgentWrapper(
  OriginalComponent: React.ComponentType,
  agentStyles: HTMLStyleElement[],
  displayName: string
) {
  const AgentWrapper: React.FC = () => {
    useEffect(() => {
      // Re-enable styles disabled on a prior unmount
      enableAgentStyles(agentStyles)
      // Track any styles injected by lazy-loaded chunks while the agent is active
      const observer = observeNewStyles(agentStyles)
      return () => {
        observer.disconnect()
        // Disable (not remove) so the module cache can re-enable them on next visit
        disableAgentStyles(agentStyles)
      }
    }, [])
    return <OriginalComponent />
  }
  AgentWrapper.displayName = displayName
  return AgentWrapper
}

export function createAgentLoader(agentName: string, importFn: ImportFn) {
  const storageKey = `${agentName}ReloadCount`
  const displayName = agentName.charAt(0).toUpperCase() + agentName.slice(1)

  // Persists across re-renders: <style> elements injected by this micro-frontend
  const agentStyles: HTMLStyleElement[] = []

  return lazy(async () => {
    // Snapshot existing styles BEFORE loading the remote so we can identify
    // which <style> tags were injected by the micro-frontend's CSS bundles.
    const stylesBefore = new Set(document.querySelectorAll('style'))

    try {
      const module = await importFn()
      sessionStorage.removeItem(storageKey)

      // Capture styles injected synchronously during module evaluation
      document.querySelectorAll<HTMLStyleElement>('style').forEach((el) => {
        if (!stylesBefore.has(el)) agentStyles.push(el)
      })

      return { default: makeAgentWrapper(module.default, agentStyles, displayName) }
    } catch (error) {
      console.error(`Error loading ${displayName} remote:`, error)

      const reloadCount = Number.parseInt(sessionStorage.getItem(storageKey) ?? '0', 10)

      if (reloadCount < 3) {
        sessionStorage.setItem(storageKey, (reloadCount + 1).toString())
        console.log(`Recargando automáticamente... (intento ${reloadCount + 1} de 3)`)
        globalThis.location.reload()
        return { default: AgentLoadingFallback }
      }

      sessionStorage.removeItem(storageKey)
      return {
        default: () => <AgentErrorFallback agentName={displayName} error={error} storageKey={storageKey} />,
      }
    }
  })
}
